import {CSSProperties, useState} from "react";
import {Tooltip, Popover} from "@mui/material";
import {RgbaColorPicker, HexColorInput} from "react-colorful";
import {RiftColor} from "../models/remoteConstants.model";
import {useDebouncedCommit} from "../utils/useDebouncedCommit";
import {LIVE_PREVIEW_DEBOUNCE_MS} from "../utils/constants";

// Editor input for a RemoteConstants RiftColor ({r,g,b,a} in 0..1). A swatch
// (over a checkerboard so alpha is visible) opens a react-colorful RGBA picker
// plus a hex text field for copy/paste (8-digit #rrggbbaa carries alpha).

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
const to255 = (n: number) => Math.round(clamp01(n) * 255);

// RiftColor (rgb+a in 0..1) <-> react-colorful RgbaColor (rgb 0..255, a 0..1).
type Rgba = { r: number; g: number; b: number; a: number };
const toRgba = (c: RiftColor): Rgba => ({r: to255(c.r), g: to255(c.g), b: to255(c.b), a: clamp01(c.a)});
const fromRgba = (c: Rgba): RiftColor => ({r: c.r / 255, g: c.g / 255, b: c.b / 255, a: c.a});

const hx = (n: number) => to255(n).toString(16).padStart(2, '0');
const riftToHex = (c: RiftColor) => `#${hx(c.r)}${hx(c.g)}${hx(c.b)}${hx(c.a)}`;

function hexToRift(hex: string): RiftColor {
    let s = hex.replace('#', '');
    // Expand 3/4-digit shorthand to full form.
    if (s.length === 3 || s.length === 4) s = s.split('').map(ch => ch + ch).join('');
    const n = (i: number) => parseInt(s.slice(i, i + 2), 16) / 255;
    return {r: n(0), g: n(2), b: n(4), a: s.length >= 8 ? n(6) : 1};
}

// Checkerboard behind translucent swatches so alpha reads visually.
const checker: CSSProperties = {
    backgroundImage:
        'linear-gradient(45deg, #808080 25%, transparent 25%), ' +
        'linear-gradient(-45deg, #808080 25%, transparent 25%), ' +
        'linear-gradient(45deg, transparent 75%, #808080 75%), ' +
        'linear-gradient(-45deg, transparent 75%, #808080 75%)',
    backgroundSize: '8px 8px',
    backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0',
    backgroundColor: '#bbb',
};

// Bare color control (swatch → popover picker), no label — for use inside a
// caller-owned layout (e.g. the Styles page grid).
export function ColorControl(props: { value: RiftColor; onChange(value: RiftColor): void }) {
    // Debounced so a picker/hex drag emits one command per settle, not per pixel
    // (each command snapshots state for undo and drives a Live Preview push).
    const [c, set] = useDebouncedCommit(
        props.value ?? {r: 0, g: 0, b: 0, a: 1}, props.onChange, LIVE_PREVIEW_DEBOUNCE_MS);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const rgba = toRgba(c);
    const css = `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${clamp01(c.a)})`;

    return (
        <>
            <Tooltip title={riftToHex(c)} arrow disableInteractive>
                <button
                    onClick={e => setAnchor(e.currentTarget)}
                    style={{
                        width: 32, height: 24, padding: 0, borderRadius: 3, cursor: 'pointer',
                        border: '1px solid #fff', ...checker,
                    }}>
                    {/* the color over the checkerboard; alpha lets the grid show through */}
                    <div style={{width: '100%', height: '100%', background: css, borderRadius: 2}}/>
                </button>
            </Tooltip>
            <Popover
                open={anchor != null}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                slotProps={{paper: {sx: {background: '#161b22', p: 1.25, overflow: 'visible'}}}}>
                <div className={'layout vertical'} style={{gap: 8}}>
                    <RgbaColorPicker color={rgba} onChange={v => set(fromRgba(v))}/>
                    <HexColorInput
                        color={riftToHex(c)}
                        alpha
                        prefixed
                        onChange={h => set(hexToRift(h))}
                        style={{
                            width: '100%', boxSizing: 'border-box', textAlign: 'center',
                            padding: '4px 6px', borderRadius: 3, border: '1px solid rgba(255,255,255,0.2)',
                            background: '#0d1117', color: 'white', fontFamily: 'monospace', textTransform: 'uppercase',
                        }}/>
                </div>
            </Popover>
        </>
    );
}

interface ColorInputLabelProps {
    title: string;
    value: RiftColor;
    tooltip?: string;
    style?: CSSProperties;
    onChange(value: RiftColor): void;
}

// Label + control row (title left, control right). Kept for callers that want a
// self-contained labelled row.
function ColorInputLabel(props: ColorInputLabelProps) {
    return (
        <div className={'layout horizontal stretch gap flex-grow'}
             style={{...{alignItems: 'center', gap: 10}, ...props.style}}>
            {props.tooltip
                ? <Tooltip title={props.tooltip} arrow disableInteractive>
                    <p className={'flex-grow'} style={{cursor: 'help'}}>{props.title}</p>
                </Tooltip>
                : <p className={'flex-grow'}>{props.title}</p>}
            <ColorControl value={props.value} onChange={props.onChange}/>
        </div>
    );
}

export default ColorInputLabel;
